import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';

export interface ImpersonationResponse {
  success: boolean;
  access_token: string;
  impersonated_company_id: number;
  impersonated_company_name: string;
  original_company_id: number;
  user_id: number;
}

export interface ImpersonationStatus {
  is_impersonating: boolean;
  impersonated_company_id?: number;
  impersonated_company_name?: string;
  original_company_id?: number;
  user_id?: number;
}

export interface ExitImpersonationResponse {
  success: boolean;
  message: string;
  restored_company_id: number;
}

const ORIGINAL_TOKEN_KEY = 'originalAuthToken';
const IMPERSONATION_DATA_KEY = 'impersonationData';

class ImpersonationService {
  /**
   * Start impersonating a tenant
   */
  async startImpersonation(tenantId: number): Promise<ImpersonationResponse> {
    // Store original token before impersonating
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
    }

    const response = await apiClient.post<ImpersonationResponse>(
      `${API_CONFIG.ENDPOINTS.IMPERSONATE}/${tenantId}`
    );

    if (response.success && response.access_token) {
      // Store impersonation data
      localStorage.setItem(IMPERSONATION_DATA_KEY, JSON.stringify({
        impersonatedCompanyId: response.impersonated_company_id,
        impersonatedCompanyName: response.impersonated_company_name,
        originalCompanyId: response.original_company_id,
      }));

      // Replace token with impersonation token
      localStorage.setItem('authToken', response.access_token);
    }

    return response;
  }

  /**
   * Exit impersonation and restore original token
   */
  async exitImpersonation(): Promise<ExitImpersonationResponse> {
    const response = await apiClient.post<ExitImpersonationResponse>(
      API_CONFIG.ENDPOINTS.IMPERSONATE_EXIT
    );

    // Restore original token
    const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    if (originalToken) {
      localStorage.setItem('authToken', originalToken);
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    }

    // Clear impersonation data
    localStorage.removeItem(IMPERSONATION_DATA_KEY);

    return response;
  }

  /**
   * Get current impersonation status from API
   */
  async getImpersonationStatus(): Promise<ImpersonationStatus> {
    return apiClient.get<ImpersonationStatus>(API_CONFIG.ENDPOINTS.IMPERSONATE_STATUS);
  }

  /**
   * Check if currently impersonating (from local storage)
   */
  isImpersonating(): boolean {
    return !!localStorage.getItem(IMPERSONATION_DATA_KEY);
  }

  /**
   * Get impersonation data from local storage
   */
  getImpersonationData(): {
    impersonatedCompanyId: number;
    impersonatedCompanyName: string;
    originalCompanyId: number;
  } | null {
    const data = localStorage.getItem(IMPERSONATION_DATA_KEY);
    if (data) {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Clear impersonation state (for logout)
   */
  clearImpersonation(): void {
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_DATA_KEY);
  }
}

export const impersonationService = new ImpersonationService();
